import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { MaskText } from './ui/Reveal';
import { MagneticButton } from './ui/MagneticButton';
import { HeroVisual } from './HeroVisual';
import { useBooking } from './BookingModal';
import { brand, images } from '../data/site';
import { usePrefersReducedMotion } from '../hooks/useMedia';
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();
  const {
    openBooking
  } = useBooking();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const typeY = useTransform(scrollYProgress, [0, 1], ['0%', '-14%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return <section ref={ref} className="relative overflow-hidden bg-surface pb-16 pt-32 sm:pb-24 sm:pt-40" aria-label="Introduction" data-id="element-264" data-mp="3cz5uuafyvxzt">
      {/* Slow-moving brand shapes */}
      <motion.div aria-hidden="true" className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full border border-lav/30" animate={reduced ? undefined : {
      y: [0, 26, 0],
      x: [0, 14, 0]
    }} transition={{
      duration: 18,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-265" data-mp="3j6fx1mlet7c9" />
      <motion.div aria-hidden="true" className="pointer-events-none absolute -right-16 bottom-16 h-40 w-40 bg-brand/10" animate={reduced ? undefined : {
      rotate: [0, 45, 0]
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-266" data-mp="3eb9150ydt6xw" />

      <div className="mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-267" data-mp="2qhqtlgwvspxc">
        <motion.div style={reduced ? undefined : {
        y: typeY
      }} data-id="element-268" data-mp="2vjpyiv89ruwe">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-269" data-mp="2hjf8tvip4mct">
            <span className="label text-brand" data-id="element-270" data-mp="2hlj8205ngx6r">Creative Agency · Brand Identity</span>
            <span className="label text-muted" data-id="element-271" data-mp="2ljz0wkoewzs3">Est. {brand.founded} · {brand.parent}</span>
          </div>

          <div className="mt-7 flex items-end justify-between gap-10 xl:gap-16" data-id="element-272" data-mp="s44itpsj1piy">
            <h1 className="min-w-0 font-display text-[15vw] font-extrabold uppercase leading-[0.85] tracking-tightest sm:text-[11vw] lg:text-[8.6vw]" data-id="element-273" data-mp="n9qeuyzsua2o">
              <MaskText text="We Bring|Ideas to" data-id="element-274" data-mp="ddnhs7jt11ml" />
              <span className="block overflow-hidden" data-id="element-275" data-mp="deyl669c87d6">
                <MaskText text="Life." delay={0.18} lineClassName="text-brand" className="inline-block" data-id="element-276" data-mp="1b15olctwntny" />
              </span>
            </h1>
            <HeroVisual className="mb-3 hidden w-[clamp(14rem,20vw,20rem)] shrink-0 lg:block" data-id="element-277" data-mp="11150zep06rdy" />
          </div>

          <div className="mt-10 grid gap-10 border-t border-line pt-8 lg:grid-cols-[1.1fr_1fr_auto] lg:items-start" data-id="element-278" data-mp="x0uy4awich3b">
            <p className="max-w-md text-base leading-relaxed text-muted sm:text-lg" data-id="element-279" data-mp="3lqm2jypo95ux">
              We create strategic identities, digital experiences and visual systems that help
              ambitious brands stand out.
            </p>
            <p className="max-w-sm font-serif text-xl italic leading-snug text-ink sm:text-2xl" data-id="element-280" data-mp="21a4sds29ic4e">
              Creative direction, brand identity and visual experiences designed to make brands
              impossible to overlook.
            </p>
            <div className="flex flex-wrap gap-3" data-id="element-281" data-mp="2b2sn8h9om60h">
              <MagneticButton onClick={openBooking} cursorLabel="Let's talk" showArrow data-id="element-282" data-mp="2g0ls96z90tz6">
                Book a Consultation
              </MagneticButton>
              <MagneticButton to="/work" variant="outline" cursorLabel="View work" data-id="element-283" data-mp="2l0zycjhahtuk">
                View Our Work
              </MagneticButton>
            </div>
          </div>
        </motion.div>

        <div className="relative mt-14 overflow-hidden sm:mt-20" data-id="element-284" data-mp="2txhbttd4m3th">
          <motion.div initial={reduced ? {
          opacity: 1
        } : {
          clipPath: 'inset(100% 0 0 0)'
        }} animate={{
          clipPath: 'inset(0% 0 0 0)',
          opacity: 1
        }} transition={{
          duration: 1.2,
          delay: 0.35,
          ease: [0.76, 0, 0.24, 1]
        }} className="relative aspect-[16/9] w-full overflow-hidden bg-surface2" data-id="element-285" data-mp="unrbv0pwrruz">
            <motion.img src={images.brandIdentity} alt="Brand identity process — Wayne & Co logo suite, colour palette, typography and brand applications open on screen" className="h-full w-full scale-110 object-cover" style={reduced ? undefined : {
            y: imageY
          }} loading="eager" decoding="async" data-id="element-286" data-mp="e9jwrrwfz5qu" />
            <div className="absolute inset-0 border border-white/10" aria-hidden="true" data-id="element-287" data-mp="3rg3k7oeuks4p" />
          </motion.div>
        </div>

        <motion.div className="mt-6 flex items-center gap-3 text-muted" style={reduced ? undefined : {
        opacity: fade
      }} data-id="element-288" data-mp="3motw4s8y5wux">
          <ArrowDownIcon className="h-4 w-4 animate-bounce" aria-hidden="true" data-id="element-289" data-mp="2yxy6a0o1xbqb" />
          <span className="label" data-id="element-290" data-mp="2u06iniufwbrx">Scroll to explore</span>
        </motion.div>
      </div>
    </section>;
}